import { computed, makeObservable, observable, runInAction } from 'mobx'
import _ from 'lodash'
import { LOADING_STATUS, TermsDetail } from '../constants'
import { HttpReq } from '../utils'

type SubStoreLoadParam = {
  param?: Record<string, any>
  data?: Record<string, any>
}

type UrlType = ((param?: Record<string, any> | any) => string) | string

const DEFAULT_PAGE_SIZE = 100

abstract class AbstractSubStore<T> {
  status: LOADING_STATUS = LOADING_STATUS.IDLE
  content?: T
  errorMessage = ''

  preLoadParam?: SubStoreLoadParam

  get isNotFound() {
    return this.status === LOADING_STATUS.NOT_FOUND
  }
  get isFailed() {
    return this.status === LOADING_STATUS.FAILED
  }
  get isLoading() {
    return this.status === LOADING_STATUS.LOADING
  }
  get isLoaded() {
    return this.status === LOADING_STATUS.LOADED
  }

  constructor(private readonly url: UrlType) {
    makeObservable(this, {
      content: observable,
      status: observable,
      isFailed: computed,
      isLoading: computed,
      isLoaded: computed,
    })
  }

  clearAll = async (refreshOnly?: boolean) => {
    return await new Promise<void>((resolve) => {
      runInAction(() => {
        if (!refreshOnly) this.status = LOADING_STATUS.IDLE
        this.errorMessage = ''
        this.content = undefined
        this.preLoadParam = undefined
        resolve()
      })
    })
  }

  private setStatus = (status: LOADING_STATUS) => {
    return new Promise<void>((resolve) => {
      runInAction(() => {
        this.status = status
        resolve()
      })
    })
  }

  async load(data?: SubStoreLoadParam, refresh?: boolean) {
    // if (this.isLoading) return
    this.setStatus(LOADING_STATUS.LOADING)
    if (refresh) await this.clearAll(refresh)

    if (data && _.isEqual(data, this.preLoadParam)) {
      runInAction(() => {
        this.status = LOADING_STATUS.LOADED
      })
      return
    }

    this.preLoadParam = data

    try {
      const response = await HttpReq<T>({
        url: typeof this.url === 'function' ? this.url(data?.param) : this.url,
        method: 'GET',
        data: data?.data,
      })

      runInAction(() => {
        this.applyStatus(response)
        this.applyContent(response)
      })

      return response
    } catch (err: any) {
      if (err.response?.data.CODE.endsWith('404')) {
        runInAction(() => {
          this.status = LOADING_STATUS.NOT_FOUND
          this.errorMessage = err.response?.data.message
        })
        throw err
      }
      runInAction(() => {
        this.status = LOADING_STATUS.FAILED
      })
      throw err
    }
  }

  protected applyStatus(content: T | null) {
    this.status = content != null ? LOADING_STATUS.LOADED : LOADING_STATUS.FAILED
  }
  protected abstract applyContent: (content: T | null) => void
}

export type BaseListParams = {
  resultCount: number
  total: number
  last: boolean
  offset: number
  size: number
  sort: string[]
}

type RequestParams = {
  offset?: number
  size?: number
  sort?: string[]
}

export type ResponseContent<T> = BaseListParams & {
  list: T[]
}

const emptyResponse = (params?: RequestParams): ResponseContent<any> => ({
  list: [],
  resultCount: 0,
  total: 0,
  last: true,
  offset: params?.offset ?? 0,
  size: params?.size ?? DEFAULT_PAGE_SIZE,
  sort: params?.sort ?? [],
})
export class SingleSubStore<T> extends AbstractSubStore<T> {
  protected applyContent = (content: T | null) => {
    this.content = content != null ? content : undefined
  }
}

// FIXME : 공통으로 사용될 수 있게 확장 필요
export class TermsSubStore<T> extends AbstractSubStore<T> {
  protected applyContent = (content: any | null) => {
    content.sort((a: TermsDetail, b: TermsDetail) => {
      if (a.required) return -1
      if (b.required) return 1
      return 0
    })

    this.content = content || undefined
  }
}

export class PagingSubStore<T> extends AbstractSubStore<ResponseContent<T>> {
  // request paging params
  offset?: number
  size?: number
  sort?: string[]

  // response contents
  list: T[] = []

  constructor(url: string | UrlType) {
    super(url)
    makeObservable(this, { list: observable })
    this.clearAll()
  }

  clearAll = async (refreshOnly?: boolean) => {
    return new Promise<void>((resolve) => {
      runInAction(() => {
        this.offset = undefined
        this.size = undefined
        this.sort = undefined
        this.preLoadParam = undefined
        this.content = emptyResponse()
        this.list = []
        if (!refreshOnly) this.status = LOADING_STATUS.IDLE
        resolve()
      })
    })
  }

  appendOffset = () => {
    this.offset = (this.offset ?? 0) + (this.size ?? DEFAULT_PAGE_SIZE)
  }

  private setContent = (content: ResponseContent<T>) => {
    this.content = content
    this.offset = content.offset
    this.size = content.size
    this.sort = content.sort
  }

  load = async (data?: SubStoreLoadParam, refresh?: boolean) => {
    if (this.isLoading) return
    if (refresh) await this.clearAll(refresh)

    return await super.load({
      param: data?.param,
      data: {
        offset: this.offset,
        size: this.size,
        sort: this.sort,
        ...data?.data,
      },
    })
  }
  protected applyStatus = (content: ResponseContent<T> | null) => {
    this.status = !content || !content?.list ? LOADING_STATUS.FAILED : LOADING_STATUS.LOADED
  }
  protected applyContent = (content: ResponseContent<T> | null) => {
    if (!content || !content?.list) {
      this.setContent(emptyResponse())
    } else {
      this.setContent(content)
      this.list.push(...content.list)
    }
  }
}
